import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, BookOpen, Sparkles } from 'lucide-react';
import { useLanguage } from '@/i18n';
import { manualAPI, usersAPI } from '@/lib/api';
import { toast } from 'sonner';

const COLORS = {
  orange: '#F6931F',
  blue: '#2B4DAA',
  blueDark: '#1E3A8A'
};

const TOUR_OPEN_DELAY_MS = 900;

const hasAnyAccess = (effectivePermissions = [], requiredPermissions = []) => {
  if (effectivePermissions.includes('*')) return true;
  return requiredPermissions.some((permission) => effectivePermissions.includes(permission));
};

const buildTourStep = ({ id, title, content, icon, path, highlight = null, position = 'bottom-right' }) => ({
  id,
  title,
  content,
  icon,
  path,
  highlight,
  position,
});

const getTourSteps = (user, t, manualInfo = null) => {
  const role = user?.role || 'staff';
  const profileName = user?.custom_role_name || role;
  const effectivePermissions = user?.effective_permissions || [];
  const profileCandidates = [user?.custom_role_key, user?.role, user?.custom_role_name, user?.job_title]
    .map((item) => String(item || '').trim().toLowerCase())
    .filter(Boolean);
  const roleGuide = (manualInfo?.profile_guides || []).find((guide) => {
    const guideCandidates = [guide?.role_key, guide?.title]
      .map((item) => String(item || '').trim().toLowerCase())
      .filter(Boolean);
    return profileCandidates.some((candidate) => guideCandidates.includes(candidate));
  }) || null;
  const moduleLookup = new Map((manualInfo?.module_overview || []).map((module) => [module.key, module]));
  const roleVisibleModules = new Map((roleGuide?.visible_modules || []).map((module) => [module.key, module]));

  const getContextualModuleText = (moduleKey, fallback) => {
    const roleModule = roleVisibleModules.get(moduleKey);
    const moduleInfo = roleModule || moduleLookup.get(moduleKey);
    if (!moduleInfo) return fallback;
    const actions = Array.isArray(roleModule?.allowed_actions) && roleModule.allowed_actions.length > 0
      ? ` Acções do seu perfil: ${roleModule.allowed_actions.slice(0, 4).join(', ')}.`
      : '';
    return `${moduleInfo.summary || fallback}.${actions}`;
  };

  const roleQuickPaths = {
    admin: ['Dashboard → KPIs', 'Centro Administrativo → acessos', 'About → publicar versão'],
    store_manager: ['Dashboard → operação da loja', 'POS/Caixa → turno', 'Inventário/Documentos → pendências'],
    supervisor: ['Dashboard → prioridades', 'Alertas/Relatórios → desvios', 'About → mudanças recentes'],
    accountant: ['Análise Financeira → margem/custos', 'Despesas/Recebíveis → período', 'Payroll/Relatórios → fecho'],
    sales: ['POS → venda', 'Documentos → emissão', 'Clientes/Inventário → validação'],
    cashier: ['Caixa → abrir sessão', 'POS/Serviços → receber', 'Caixa → fechar sessão'],
    receptionist: ['Serviços → abrir atendimento', 'Clientes → contactos', 'Serviços → estado/entrega'],
    technician: ['Serviços → diagnóstico', 'Inventário → peças', 'Serviços → conclusão'],
    tech_lead: ['Obras → progresso', 'Serviços → bloqueios', 'Feedback → melhoria'],
    procurement: ['Compras → ordens', 'Fornecedores → condições', 'Pricing → impacto'],
    warehouse: ['Inventário → stock crítico', 'Transferências → movimentos', 'Kits → disponibilidade'],
    customer_support: ['Clientes → ocorrências', 'Feedback → follow-up', 'Licenças → estado'],
    staff: ['Dashboard → contexto do turno', 'Módulo autorizado → execução', 'About → mudanças recentes'],
  };
  const quickPathText = ((roleGuide?.tour_quick_path && roleGuide.tour_quick_path.length > 0 ? roleGuide.tour_quick_path : (roleQuickPaths[role] || roleQuickPaths.staff))).join(' • ');

  const baseSteps = [
    buildTourStep({
      id: 'welcome',
      title: t('welcomeTour.steps.welcome.title'),
      content: `${roleGuide?.about_context || t('welcomeTour.steps.welcome.content', { profileName })} Roteiro rápido: ${quickPathText}.`,
      icon: '🎉',
      path: '/dashboard',
      position: 'center',
    }),
    buildTourStep({
      id: 'dashboard',
      title: t('welcomeTour.steps.dashboard.title'),
      content: getContextualModuleText('dashboard', t('welcomeTour.steps.dashboard.content')),
      icon: '📊',
      path: '/dashboard',
      highlight: '[data-testid="nav-dashboard"]',
    }),
  ];

  const salesSteps = [
    buildTourStep({
      id: 'sales',
      title: t('welcomeTour.steps.sales.title'),
      content: getContextualModuleText('sales', t('welcomeTour.steps.sales.content')),
      icon: '💰',
      path: '/sales?tab=sales',
      highlight: '[data-testid="nav-sales"]',
    }),
    buildTourStep({
      id: 'inventory',
      title: t('welcomeTour.steps.inventory.title'),
      content: getContextualModuleText('inventory', t('welcomeTour.steps.inventory.content')),
      icon: '📦',
      path: '/inventory',
      highlight: '[data-testid="nav-inventory"]',
    }),
    buildTourStep({
      id: 'clients',
      title: t('welcomeTour.steps.clients.title'),
      content: getContextualModuleText('clients', t('welcomeTour.steps.clients.content')),
      icon: '👤',
      path: '/clients',
      highlight: '[data-testid="nav-clients"]',
    }),
  ];

  const serviceSteps = [
    buildTourStep({
      id: 'services',
      title: t('welcomeTour.steps.services.title'),
      content: getContextualModuleText('services', t('welcomeTour.steps.services.content')),
      icon: '🔧',
      path: '/services',
      highlight: '[data-testid="nav-services"]',
    }),
    buildTourStep({
      id: 'projects',
      title: t('welcomeTour.steps.projects.title'),
      content: getContextualModuleText('projects', t('welcomeTour.steps.projects.content')),
      icon: '🏗️',
      path: '/services?module=obras',
      highlight: '[data-testid="nav-services"]',
    }),
  ];

  const cashSteps = [
    buildTourStep({
      id: 'cash',
      title: t('welcomeTour.steps.cash.title'),
      content: getContextualModuleText('cash', t('welcomeTour.steps.cash.content')),
      icon: '💵',
      path: '/sales?tab=cash',
      highlight: '[data-testid="nav-cash"]',
    }),
  ];

  const documentSteps = [
    buildTourStep({
      id: 'documents',
      title: t('welcomeTour.steps.documents.title'),
      content: getContextualModuleText('documents', t('welcomeTour.steps.documents.content')),
      icon: '🧾',
      path: '/documents',
      highlight: '[data-testid="nav-documents"]',
    }),
  ];

  const expensesSteps = [
    buildTourStep({
      id: 'expenses',
      title: t('welcomeTour.steps.expenses.title'),
      content: getContextualModuleText('expenses', t('welcomeTour.steps.expenses.content')),
      icon: '💸',
      path: '/expenses',
      highlight: '[data-testid="nav-expenses"]',
    }),
  ];

  const payrollSteps = [
    buildTourStep({
      id: 'payroll',
      title: t('welcomeTour.steps.payroll.title'),
      content: getContextualModuleText('payroll', t('welcomeTour.steps.payroll.content')),
      icon: '🧑‍💼',
      path: '/payroll',
      highlight: '[data-testid="nav-payroll"]',
    }),
  ];

  const procurementSteps = [
    buildTourStep({
      id: 'procurement',
      title: t('welcomeTour.steps.procurement.title'),
      content: getContextualModuleText('procurement', t('welcomeTour.steps.procurement.content')),
      icon: '🛒',
      path: '/procurement',
      highlight: '[data-testid="nav-procurement"]',
    }),
  ];

  const pricingSteps = [
    buildTourStep({
      id: 'pricing',
      title: t('welcomeTour.steps.pricing.title'),
      content: getContextualModuleText('pricing', t('welcomeTour.steps.pricing.content')),
      icon: '📉',
      path: '/pricing',
      highlight: '[data-testid="nav-pricing"]',
    }),
  ];

  const reportSteps = [
    buildTourStep({
      id: 'reports',
      title: t('welcomeTour.steps.reports.title'),
      content: getContextualModuleText('reports', t('welcomeTour.steps.reports.content')),
      icon: '📑',
      path: '/reports',
      highlight: '[data-testid="nav-reports"]',
    }),
  ];

  const feedbackSteps = [
    buildTourStep({
      id: 'feedback',
      title: t('welcomeTour.steps.feedback.title'),
      content: t('welcomeTour.steps.feedback.content'),
      icon: '💬',
      path: '/feedback',
      highlight: '[data-testid="nav-feedback"]',
    }),
  ];

  const adminSteps = [
    buildTourStep({
      id: 'users',
      title: t('welcomeTour.steps.users.title'),
      content: t('welcomeTour.steps.users.content'),
      icon: '👥',
      path: '/admin-center?tab=users',
      highlight: '[data-testid="nav-adminCenter"]',
    }),
    buildTourStep({
      id: 'integrity',
      title: t('welcomeTour.steps.integrity.title'),
      content: t('welcomeTour.steps.integrity.content'),
      icon: '🛡️',
      path: '/integrity',
      highlight: '[data-testid="nav-integrity"]',
    }),
    buildTourStep({
      id: 'settings',
      title: t('welcomeTour.steps.settings.title'),
      content: t('welcomeTour.steps.settings.content'),
      icon: '⚙️',
      path: '/settings?tab=payment-methods',
      highlight: '[data-testid="nav-settings"]',
    }),
    buildTourStep({
      id: 'templateBuilder',
      title: t('welcomeTour.steps.templateBuilder.title'),
      content: t('welcomeTour.steps.templateBuilder.content'),
      icon: '🧩',
      path: '/template-builder',
      highlight: '[data-testid="nav-settings"]',
    }),
  ];

  const financialSteps = [
    buildTourStep({
      id: 'financial',
      title: t('welcomeTour.steps.financial.title'),
      content: t('welcomeTour.steps.financial.content'),
      icon: '📈',
      path: '/financial-analysis',
      highlight: '[data-testid="nav-financialAnalysis"]',
    }),
  ];

  const stockTransferSteps = [
    buildTourStep({
      id: 'stockTransfers',
      title: t('welcomeTour.steps.stockTransfers.title'),
      content: getContextualModuleText('stock_transfers', t('welcomeTour.steps.stockTransfers.content')),
      icon: '↔️',
      path: '/stock-transfers',
      highlight: '[data-testid="nav-stockTransfers"]',
    }),
  ];

  const receivablesSteps = [
    buildTourStep({
      id: 'receivables',
      title: t('welcomeTour.steps.receivables.title'),
      content: t('welcomeTour.steps.receivables.content'),
      icon: '💳',
      path: '/receivables',
      highlight: '[data-testid="nav-receivables"]',
    }),
  ];

  const supplierSteps = [
    buildTourStep({
      id: 'suppliers',
      title: t('welcomeTour.steps.suppliers.title'),
      content: getContextualModuleText('suppliers', t('welcomeTour.steps.suppliers.content')),
      icon: '🏢',
      path: '/suppliers',
      highlight: '[data-testid="nav-suppliers"]',
    }),
  ];

  const alertsSteps = [
    buildTourStep({
      id: 'alerts',
      title: t('welcomeTour.steps.alerts.title'),
      content: getContextualModuleText('alerts', t('welcomeTour.steps.alerts.content')),
      icon: '🚨',
      path: '/alerts',
      highlight: '[data-testid="nav-alerts"]',
    }),
  ];

  const licensesSteps = [
    buildTourStep({
      id: 'licenses',
      title: t('welcomeTour.steps.licenses.title'),
      content: getContextualModuleText('licenses', t('welcomeTour.steps.licenses.content')),
      icon: '🔐',
      path: '/licenses',
      highlight: '[data-testid="nav-licenses"]',
    }),
  ];

  const finalStep = buildTourStep({
    id: 'about',
    title: t('welcomeTour.steps.about.title'),
    content: `${getContextualModuleText('about', t('welcomeTour.steps.about.content'))} ${(roleGuide?.access_limits || []).slice(0, 1).join(' ')}`.trim(),
    icon: '📚',
    path: '/about',
    highlight: '[data-testid="nav-about"]',
  });

  let steps = [...baseSteps];

  if (hasAnyAccess(effectivePermissions, ['sales.view', 'sales.create', 'documents.view', 'clients.view'])) {
    steps = [...steps, ...salesSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['services.view', 'services.create', 'services.edit'])) {
    steps = [...steps, ...serviceSteps.filter((step) => step.id !== 'projects')];
  }

  if (hasAnyAccess(effectivePermissions, ['projects.view', 'projects.create', 'projects.edit'])) {
    steps = [...steps, ...serviceSteps.filter((step) => step.id === 'projects')];
  }

  if (hasAnyAccess(effectivePermissions, ['cash.view'])) {
    steps = [...steps, ...cashSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['documents.view'])) {
    steps = [...steps, ...documentSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['inventory.transfer'])) {
    steps = [...steps, ...stockTransferSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['receivables.view'])) {
    steps = [...steps, ...receivablesSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['expenses.view'])) {
    steps = [...steps, ...expensesSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['payroll.view'])) {
    steps = [...steps, ...payrollSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['procurement.view'])) {
    steps = [...steps, ...procurementSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['pricing.view'])) {
    steps = [...steps, ...pricingSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['suppliers.view'])) {
    steps = [...steps, ...supplierSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['financial_analysis.view'])) {
    steps = [...steps, ...financialSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['reports.view'])) {
    steps = [...steps, ...reportSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['feedback.view'])) {
    steps = [...steps, ...feedbackSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['alerts.view'])) {
    steps = [...steps, ...alertsSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['licenses.view'])) {
    steps = [...steps, ...licensesSteps];
  }

  if (hasAnyAccess(effectivePermissions, ['users.view', 'roles.view', 'settings.view', 'integrity.view'])) {
    steps = [...steps, ...adminSteps.filter((step) => {
      if (step.id === 'users') return hasAnyAccess(effectivePermissions, ['users.view']);
      if (step.id === 'integrity') return hasAnyAccess(effectivePermissions, ['integrity.view']);
      if (step.id === 'settings') return hasAnyAccess(effectivePermissions, ['settings.view']);
      if (step.id === 'templateBuilder') return hasAnyAccess(effectivePermissions, ['settings.view']);
      return true;
    })];
  }

  if (steps.length <= 2) {
    switch (role) {
      case 'accountant':
        steps = [...steps, ...financialSteps, ...expensesSteps, ...payrollSteps, ...reportSteps, ...documentSteps, ...receivablesSteps];
        break;
      case 'procurement':
        steps = [...steps, ...procurementSteps, ...pricingSteps, ...supplierSteps, ...salesSteps.filter((step) => step.id === 'inventory')];
        break;
      case 'warehouse':
        steps = [...steps, ...salesSteps.filter((step) => step.id === 'inventory'), ...stockTransferSteps];
        break;
      case 'technician':
        steps = [...steps, ...serviceSteps];
        break;
      case 'receptionist':
        steps = [...steps, ...serviceSteps.filter((step) => step.id === 'services'), ...salesSteps.filter((step) => step.id === 'clients')];
        break;
      case 'cashier':
        steps = [...steps, ...cashSteps, ...salesSteps.filter((step) => step.id === 'sales')];
        break;
      case 'customer_support':
        steps = [...steps, ...salesSteps.filter((step) => step.id === 'clients'), ...feedbackSteps, ...documentSteps];
        break;
      case 'sales':
      case 'store_manager':
      case 'staff':
      default:
        steps = [...steps, ...salesSteps, ...documentSteps];
        break;
    }
  }

  if (hasAnyAccess(effectivePermissions, ['about.view']) || steps.length > 0) {
    steps = [...steps, finalStep];
  }

  return steps.filter((step, index, array) => array.findIndex((candidate) => candidate.id === step.id) === index);
};

function TourModal({ step, steps, currentStep, totalSteps, isSaving, onNext, onPrev, onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  useEffect(() => {
    let element = null;
    if (step.highlight) {
      element = document.querySelector(step.highlight);
      if (element) {
        element.style.boxShadow = `0 0 0 4px ${COLORS.orange}`;
        element.style.borderRadius = '8px';
        element.style.transition = 'box-shadow 0.3s ease';
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return () => {
      if (element) {
        element.style.boxShadow = '';
        element.style.borderRadius = '';
        element.style.transition = '';
      }
    };
  }, [step]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = steps[currentStep + 1];
      if (nextStep?.path) {
        navigate(nextStep.path);
      }
      onNext();
    } else {
      onClose(true);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prevStep = steps[currentStep - 1];
      if (prevStep?.path) {
        navigate(prevStep.path);
      }
      onPrev();
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-slate-900/70 backdrop-blur-sm" data-testid="welcome-tour-overlay" />

      <div className={`fixed z-[9999] ${step.position === 'center' ? 'inset-0 flex items-center justify-center' : 'bottom-8 right-8'}`}>
        <div 
          className={`bg-white rounded-2xl shadow-2xl overflow-hidden ${step.position === 'center' ? 'w-[480px]' : 'w-[400px]'}`}
          style={{ border: `3px solid ${COLORS.orange}` }}
          data-testid="welcome-tour-modal"
        >
          <div 
            className="px-6 py-4 flex items-center justify-between"
            style={{ background: `linear-gradient(135deg, ${COLORS.blue} 0%, ${COLORS.blueDark} 100%)` }}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">{step.icon}</span>
              <div>
                <h3 className="font-bold text-white text-lg" data-testid="welcome-tour-step-title">{step.title}</h3>
                <p className="text-blue-200 text-xs" data-testid="welcome-tour-step-counter">
                  {t('welcomeTour.stepCounter', { current: currentStep + 1, total: totalSteps })}
                </p>
              </div>
            </div>
            <button 
              onClick={() => onClose(false)}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="welcome-tour-close-button"
              disabled={isSaving}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="h-1 bg-slate-200">
            <div 
              className="h-full transition-all duration-300"
              style={{ 
                width: `${((currentStep + 1) / totalSteps) * 100}%`,
                background: COLORS.orange
              }}
              data-testid="welcome-tour-progress-bar"
            />
          </div>
          
          <div className="p-6">
            <p className="text-slate-600 leading-relaxed mb-6" data-testid="welcome-tour-step-content">
              {step.content}
            </p>
            
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => onClose(false)}
                className="text-slate-500 hover:text-slate-700"
                data-testid="welcome-tour-skip-button"
                disabled={isSaving}
              >
                {t('welcomeTour.skip')}
              </Button>
              
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    className="gap-1"
                    data-testid="welcome-tour-prev-button"
                    disabled={isSaving}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('actions.previous')}
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  className="gap-1"
                  style={{ 
                    background: `linear-gradient(135deg, ${COLORS.orange} 0%, #E8850F 100%)`,
                    border: 'none'
                  }}
                  data-testid="welcome-tour-next-button"
                  disabled={isSaving}
                >
                  {currentStep === totalSteps - 1 ? (
                    <>
                      {t('welcomeTour.finish')}
                      <Sparkles className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      {t('actions.next')}
                      <ChevronRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function WelcomeTour() {
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSavingPreference, setIsSavingPreference] = useState(false);
  const [manualInfo, setManualInfo] = useState(null);
  const openTimerRef = useRef(null);
  const hasOpenedThisSessionRef = useRef(false);
  const userKey = user?.id || user?.email || '';
  const steps = useMemo(() => getTourSteps(user, t, manualInfo), [manualInfo, t, user]);

  useEffect(() => {
    if (location.pathname !== '/about' && location.pathname !== '/dashboard') {
      setManualInfo(null);
      return undefined;
    }

    let active = true;
    const loadManualInfo = async () => {
      try {
        const response = await manualAPI.getInfo();
        if (active) {
          setManualInfo(response?.data || null);
        }
      } catch {
        if (active) {
          setManualInfo(null);
        }
      }
    };
    loadManualInfo();
    return () => {
      active = false;
    };
  }, [location.pathname]);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearOpenTimer();
    hasOpenedThisSessionRef.current = false;
    setIsActive(false);
    setCurrentStep(0);
    if (userKey) {
      localStorage.removeItem(`cgbusiness_tour_${userKey}`);
    }
  }, [clearOpenTimer, userKey]);

  useEffect(() => {
    if (!user || user.tour_completed !== false || steps.length === 0 || isActive || hasOpenedThisSessionRef.current || location.pathname !== '/dashboard') {
      clearOpenTimer();
      return undefined;
    }

    openTimerRef.current = window.setTimeout(() => {
      hasOpenedThisSessionRef.current = true;
      setCurrentStep(0);
      setIsActive(true);
      openTimerRef.current = null;
    }, TOUR_OPEN_DELAY_MS);

    return () => {
      clearOpenTimer();
    };
  }, [clearOpenTimer, isActive, location.pathname, steps.length, user]);

  useEffect(() => {
    if (!user || user.tour_completed !== false) {
      clearOpenTimer();
      setIsActive(false);
      setCurrentStep(0);
    }
  }, [clearOpenTimer, user]);

  useEffect(() => {
    if (currentStep < steps.length) return;
    setCurrentStep(0);
  }, [currentStep, steps.length]);

  const persistTourCompletion = useCallback(async (nextCompleted) => {
    const response = await usersAPI.updateMyWelcomeTourPreference(nextCompleted);
    updateUser({
      tour_completed: response?.data?.tour_completed ?? nextCompleted,
      tour_completed_at: response?.data?.tour_completed_at ?? (nextCompleted ? new Date().toISOString() : null),
    });
  }, [updateUser]);

  const handleNext = useCallback(() => {
    setCurrentStep((previousStep) => previousStep + 1);
  }, []);

  const handlePrev = useCallback(() => {
    setCurrentStep((previousStep) => previousStep - 1);
  }, []);

  const handleClose = useCallback(async (completedJourney) => {
    clearOpenTimer();
    setIsSavingPreference(true);
    setIsActive(false);
    setCurrentStep(0);

    try {
      await persistTourCompletion(true);
      if (completedJourney) {
        navigate('/about');
      }
    } catch (error) {
      hasOpenedThisSessionRef.current = false;
      toast.error(error?.response?.data?.detail || 'Não foi possível guardar o estado do tour.');
    } finally {
      setIsSavingPreference(false);
    }
  }, [clearOpenTimer, navigate, persistTourCompletion]);

  useEffect(() => {
    window.resetWelcomeTour = async () => {
      if (!user) return;

      clearOpenTimer();
      setIsSavingPreference(true);
      try {
        await persistTourCompletion(false);
        hasOpenedThisSessionRef.current = false;
        setIsActive(false);
        setCurrentStep(0);
        navigate('/dashboard');
      } finally {
        setIsSavingPreference(false);
      }
    };

    return () => {
      delete window.resetWelcomeTour;
    };
  }, [clearOpenTimer, navigate, persistTourCompletion, user]);

  if (!isActive || steps.length === 0) return null;

  const currentStepData = steps[currentStep];
  if (!currentStepData) return null;

  return (
    <TourModal
      step={currentStepData}
      steps={steps}
      currentStep={currentStep}
      totalSteps={steps.length}
      isSaving={isSavingPreference}
      onNext={handleNext}
      onPrev={handlePrev}
      onClose={handleClose}
    />
  );
}

export const resetWelcomeTour = async () => {
  if (window.resetWelcomeTour) {
    await window.resetWelcomeTour();
  }
};
